import * as React from 'react'
import { FieldError, UseFormReturn } from 'react-hook-form'
import { Autocomplete, SxProps, TextField, Theme } from '@mui/material'

import { useAppDispatch, useAppSelector } from '#/app/store/store'
import { CompanyData } from '#/features/register-business/api/get-company-date/types'
import { getDataCompany } from '#/features/register-business/model/stepper-slice'
import { InputStyleDark, InputStyleLight } from '#/shared'

export interface CompanyAutocompleteProps {
	methods: UseFormReturn<any, any>
	fieldName?: string
	validationRules?: any
	inModal?: boolean
	onCompanySelect?: (company: CompanyData | null | string) => void
	onOpen?: () => void
	onClose?: () => void
	defaultValue?: string
	placeholder?: string
	// Пропсы для кастомизации стилей
	error?: FieldError | undefined
	sx?: SxProps<Theme>
	textFieldSx?: SxProps<Theme>
	paperSx?: SxProps<Theme>
	popperSx?: SxProps<Theme>
	className?: string
}

export const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({
	methods,
	fieldName = 'companyName',
	validationRules,
	inModal = false,
	onCompanySelect,
	defaultValue = '',
	placeholder = '',
	error,
	sx,
	textFieldSx,
	paperSx,
	popperSx,
	className,
	onOpen,
	onClose,
}) => {
	const suggestedCompany = useAppSelector((state) => state.stepper.company)
	const dispatch = useAppDispatch()

	const [selected, setSelected] = React.useState<CompanyData | null | string>(() => {
		if (defaultValue) {
			return suggestedCompany.filter((el) => el.value === defaultValue)[0] || null
		}
		return null
	})

	// Отслеживание ввода и загрузка данных компаний
	const watchedCompanyName = methods.watch(fieldName)

	React.useEffect(() => {
		if (watchedCompanyName && watchedCompanyName.trim()) {
			dispatch(getDataCompany(watchedCompanyName))
		}
	}, [watchedCompanyName, dispatch])

	// Callback при выборе компании
	const handleChange = (event: any, value: CompanyData | null | string) => {
		setSelected(value)
		if (onCompanySelect) {
			onCompanySelect(value)
		}
	}

	const handleAutocompleteOpen = React.useCallback(
		(event: React.SyntheticEvent<Element, Event>) => {
			if (onOpen) {
				onOpen()
			}
		},
		[onOpen]
	)

	const handleAutocompleteClose = React.useCallback(
		(event: React.SyntheticEvent<Element, Event>, reason: string) => {
			if (onClose) {
				onClose()
			}
		},
		[onClose]
	)

	return (
		<Autocomplete
			freeSolo
			disablePortal={!inModal}
			clearText={'Очистить'}
			noOptionsText={'Не найдено'}
			options={suggestedCompany}
			value={selected}
			onChange={handleChange}
			onOpen={handleAutocompleteOpen}
			onClose={handleAutocompleteClose}
			getOptionLabel={(label) => (typeof label !== 'string' ? label.value : '')}
			className={className}
			sx={{
				'& .MuiAutocomplete-clearIndicator': {
					color: 'white',
				},
				...sx, // Кастомные стили переопределяют дефолтные
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={placeholder}
					error={!!error}
					helperText={error?.message}
					fullWidth
					sx={{
						...InputStyleDark,
						'& .MuiInputBase-input': {
							color: 'white',
						},
						'& .MuiInputLabel-root': {
							color: 'rgba(255,255,255,0.7)',
						},
						...textFieldSx, // Кастомные стили для TextField
					}}
					{...methods.register(fieldName, validationRules)}
				/>
			)}
			slotProps={{
				popper: {
					placement: 'bottom-start',
					modifiers: [
						{
							name: 'flip',
							enabled: false,
						},
						{
							name: 'preventOverflow',
							enabled: true,
							options: {
								altAxis: true,
								altBoundary: false,
								tether: false,
								padding: 8,
							},
						},
					],
					sx: {
						...(inModal && { zIndex: 99999 }),
						'& .MuiPaper-root': {
							width: 'fit-content',
							minWidth: '100%',
							maxWidth: '100%',
						},
						...popperSx, // Кастомные стили для Popper
					},
				},
			}}
			componentsProps={{
				paper: {
					sx: {
						backgroundColor: '#333',
						color: 'white',
						'& .MuiAutocomplete-listbox': {
							maxHeight: '300px',
							overflow: 'auto',
						},
						'& .MuiAutocomplete-option': {
							whiteSpace: 'normal',
							wordBreak: 'break-word',
							overflowWrap: 'anywhere',
							hyphens: 'auto',
							'&:hover': {
								backgroundColor: '#444',
							},
							'&[aria-selected="true"]': {
								backgroundColor: '#555',
							},
						},
						...paperSx, // Кастомные стили для Paper
					},
				},
			}}
		/>
	)
}
